import { useTranslation } from '../i18n';

/** Render a single value as a readable string */
const formatValue = (val: unknown): string => {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'boolean') return val ? '✓' : '✗';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);

  return JSON.stringify(val);
};

interface IAuditExpandedDetailProps {
  beforeValues: Record<string, any> | null;
  afterValues: Record<string, any> | null;
  metadata: Record<string, any> | null;
}

/** Expanded detail row — metadata on top, then side-by-side before/after JSON */
const AuditExpandedDetail = ({
  beforeValues,
  afterValues,
  metadata,
}: IAuditExpandedDetailProps) => {
  const { t } = useTranslation();
  const metaEntries = metadata ? Object.entries(metadata) : [];

  return (
    <div className="space-y-4">
      {/* Metadata as key-value pairs */}
      {metaEntries.length > 0 && (
        <div className="rounded-lg border border-border bg-white p-4 grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2">
          {metaEntries.map(([key, val]) => (
            <div key={key} className="flex gap-2 text-xs">
              <span className="font-mono text-muted-foreground min-w-[120px]">
                {key}:
              </span>
              <span className="font-mono text-foreground break-all">
                {formatValue(val)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Side-by-side before/after JSON */}
      {(beforeValues || afterValues) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
          <div className="flex flex-col">
            <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
              {t('audit.before')}
            </div>
            <pre className="flex-1 bg-white border border-border rounded-lg p-4 text-xs text-[#364153] overflow-auto max-h-80 font-mono leading-relaxed">
              {beforeValues ? JSON.stringify(beforeValues, null, 2) : '—'}
            </pre>
          </div>
          <div className="flex flex-col">
            <div className="text-xs font-medium text-muted-foreground mb-2 uppercase tracking-wide">
              {t('audit.after')}
            </div>
            <pre className="flex-1 bg-white border border-border rounded-lg p-4 text-xs text-[#364153] overflow-auto max-h-80 font-mono leading-relaxed">
              {afterValues ? JSON.stringify(afterValues, null, 2) : '—'}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditExpandedDetail;
